// Fixed version of AssetsStep.jsx
import React, { useRef, useState } from 'react';
import {
  Grid,
  Typography,
  Box,
  Paper,
  InputAdornment,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Image as ImageIcon,
  Movie as VideoIcon,
  AudioFile as AudioIcon,
  UploadFile as FileIcon,
  AddLink as LinkIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Edit as EditIcon,
} from '@mui/icons-material';

import AssetItem from './AssetItem';
import { ASSET_TYPES } from '../form-utils/contentFormData';
import { Card } from '../../../../design-system';
import { Button } from '../../../../design-system/components/core';

const AssetsStep = ({ 
  formik, 
  tempAssets = [], 
  setTempAssets,
  isUploading = false,
  onRemoveAsset = null,
}) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  const [activeAssetType, setActiveAssetType] = useState(ASSET_TYPES[0]);
  const [dragOver, setDragOver] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');

  // Handle file selection from input
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;
    
    // Create temporary assets
    const newAssets = files.map(file => ({
      file,
      type: getAssetTypeFromFile(file),
      originalname: file.name,
      size: file.size,
      mimetype: file.type,
      url: URL.createObjectURL(file),
      _id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }));
    
    setTempAssets([...tempAssets, ...newAssets]);
    
    // Reset file input
    event.target.value = '';
  };

  // Determine asset type from file
  const getAssetTypeFromFile = (file) => {
    const mimeType = file.type.toLowerCase();
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('audio/')) return 'audio';
    return 'document';
  };
  
  // Handle drag and drop events
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    
    // Create temporary assets
    const newAssets = files.map(file => ({
      file,
      type: getAssetTypeFromFile(file),
      originalname: file.name,
      size: file.size,
      mimetype: file.type,
      url: URL.createObjectURL(file),
      _id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    }));
    
    setTempAssets([...tempAssets, ...newAssets]);
  };
  
  // Handle asset removal
  const handleAssetRemove = (asset) => {
    // If using custom remove handler
    if (onRemoveAsset) {
      onRemoveAsset(asset);
      return;
    }
    
    // If it's a temp asset (has file property)
    if (asset.file) {
      setTempAssets(tempAssets.filter(a => a._id !== asset._id));
    } 
    // If it's an existing asset
    else {
      const updatedAssets = formik.values.assets.filter(a => a._id !== asset._id);
      formik.setFieldValue('assets', updatedAssets);
    }
    
    // Revoke object URL to prevent memory leaks
    if (asset.file && asset.url) {
      URL.revokeObjectURL(asset.url);
    }
  };
  
  // Handle link addition
  const handleAddLink = () => {
    if (!linkUrl) return;
    
    const newLink = {
      url: linkUrl,
      title: linkTitle || linkUrl,
      type: 'link',
      _id: `temp_link_${Date.now()}`
    };
    
    formik.setFieldValue('link', newLink);
    setLinkUrl('');
    setLinkTitle('');
    setLinkDialogOpen(false);
  };
  
  // Get file input accept attribute
  const getFileAccept = () => {
    return activeAssetType.accept;
  };
  
  // Active upload area background
  const getUploadAreaBackground = () => {
    if (dragOver) {
      return alpha(theme.palette.primary.main, 0.1);
    }
    return alpha(theme.palette.background.default, 0.5);
  };
  
  // Toggle file picker with selected asset type
  const openFilePicker = (assetType) => {
    setActiveAssetType(assetType);
    setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }, 100);
  };
  
  // Get all assets without duplication
  // FIX: Check if tempAssets are already included in formik.values.assets to avoid duplication
  const allAssets = (() => {
    const existingAssets = formik.values.assets || [];
    
    // If formik.values.assets already contains the tempAssets (by checking _id)
    // this means we're in the case where the parent is passing the same data to both props
    const tempIds = new Set(tempAssets.map(asset => asset._id));
    const existingIds = new Set(existingAssets.map(asset => asset._id));
    
    // Check if all temp assets are already in existing assets
    const allTempIncluded = tempAssets.length > 0 && 
                           tempAssets.every(asset => existingIds.has(asset._id));
    
    if (allTempIncluded) {
      // If temp assets are already included in formik.values.assets, just return the existing assets
      return existingAssets;
    } else {
      // Otherwise, combine them (normal case)
      return [...existingAssets, ...tempAssets];
    }
  })();

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Content Assets
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Add images, videos, and other media to enrich your content.
        </Typography>
      </Grid>
      
      {/* Asset Type Buttons */}
      <Grid item xs={12}>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 2, 
          mb: 2,
          pb: 2,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          {ASSET_TYPES.map(assetType => (
            <Button
              key={assetType.value}
              variant="outlined"
              startIcon={
                assetType.value === 'image' ? <ImageIcon /> :
                assetType.value === 'video' ? <VideoIcon /> :
                assetType.value === 'audio' ? <AudioIcon /> :
                <FileIcon />
              }
              onClick={() => openFilePicker(assetType)}
              sx={{ minWidth: 120 }}
            >
              {assetType.label}
            </Button>
          ))}
          
          <Button
            variant="outlined"
            startIcon={<LinkIcon />}
            onClick={() => setLinkDialogOpen(true)}
            sx={{ minWidth: 120 }}
          >
            Add Link
          </Button>
        </Box>
        
        {/* Hidden file input */}
        <input 
          ref={fileInputRef}
          type="file"
          multiple
          accept={getFileAccept()}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />
      </Grid>
      
      {/* Drag & Drop Upload Area */}
      <Grid item xs={12}>
        <Box
          sx={{
            border: `2px dashed ${dragOver ? theme.palette.primary.main : theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            backgroundColor: getUploadAreaBackground(),
            p: 3,
            textAlign: 'center',
            transition: 'all 0.3s ease',
            cursor: 'pointer',
            mb: 3
          }}
          onClick={() => fileInputRef.current?.click()}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          {isUploading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body1">Uploading assets...</Typography>
            </Box>
          ) : (
            <>
              <UploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
              <Typography variant="h6">
                Drag & Drop Files Here
              </Typography>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Or click to browse files
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Supported formats: Images (JPG, PNG, SVG), Videos (MP4, MOV), 
                Audio (MP3, WAV), Documents (PDF, DOC, DOCX)
              </Typography>
            </>
          )}
        </Box>
      </Grid>
      
      {/* Assets Preview */}
      <Grid item xs={12}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <InfoIcon fontSize="small" sx={{ mr: 0.5 }} />
            {allAssets.length === 0 
              ? 'No assets added yet' 
              : `${allAssets.length} Assets Added`
            }
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          {/* Existing Assets */}
          {formik.values.assets?.map((asset, index) => (
            <Grid item xs={12} sm={6} md={4} key={`existing-${asset._id || index}`}>
              <AssetItem 
                asset={asset} 
                onRemove={handleAssetRemove}
              />
            </Grid>
          ))}
          
          {/* Temporary Assets - Only render if they're not already in formik.values.assets */}
          {tempAssets.length > 0 && !tempAssets.every(temp => 
            (formik.values.assets || []).some(asset => asset._id === temp._id)
          ) && tempAssets.map((asset, index) => (
            <Grid item xs={12} sm={6} md={4} key={`temp-${asset._id || index}`}>
              <AssetItem 
                asset={asset} 
                onRemove={handleAssetRemove}
                isTemp={true}
              />
            </Grid>
          ))}
        </Grid>
        
        {/* Empty State */}
        {allAssets.length === 0 && (
          <Paper 
            sx={{ 
              p: 4, 
              textAlign: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.05)
            }}
          >
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                flexDirection: 'column'
              }}
            >
              <ImageIcon 
                sx={{ 
                  fontSize: 48, 
                  color: alpha(theme.palette.primary.main, 0.5), 
                  mb: 2 
                }} 
              />
              <Typography variant="body1" gutterBottom>
                No assets have been added yet
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Upload images, videos, or documents to enhance your content
              </Typography>
            </Box>
          </Paper>
        )}
      </Grid>
      
      {/* Link Information */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Divider sx={{ my: 2 }} />
        <Typography variant="h6" gutterBottom>
          External Link (Optional)
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          Add a link to your content for directing users to external resources.
        </Typography>
        
        {/* Link display or form */}
        {formik.values.link?.url ? (
          <Card level={1} sx={{ mb: 2 }}>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LinkIcon  sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="subtitle2">
                      {formik.values.link.title || 'External Link'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {formik.values.link.url}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Tooltip title="View Link">
                    <IconButton 
                      size="small" 
                      href={formik.values.link.url} 
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Link">
                    <IconButton 
                      size="small"
                      onClick={() => {
                        setLinkUrl(formik.values.link.url);
                        setLinkTitle(formik.values.link.title);
                        setLinkDialogOpen(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove Link">
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => formik.setFieldValue('link', null)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Box>
          </Card>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Link URL"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="https://example.com"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon />
                    </InputAdornment>
                  ),
                  endAdornment: linkUrl && (
                    <InputAdornment position="end">
                      <Button 
                        size="small" 
                        onClick={handleAddLink}
                        disabled={!linkUrl}
                      >
                        Add
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Link Title (Optional)"
                value={linkTitle}
                onChange={(e) => setLinkTitle(e.target.value)}
                placeholder="Enter a title for the link"
              />
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default AssetsStep;